import { ReactNode } from 'react';

import { useDrawdownRepaymentSchedule } from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOfferInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOfferInterestOnlyPeriod';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { Text } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { FormattedCurrency } from '../../../../../../iwIntlProvider/FormattedCurrency';
import { VariableRate } from '../../../../../ui/rate';
import { getNonInterestOnlyPaymentAmount } from '../../../../MyOffers.helpers';

export const SummaryRow = ({
    label,
    value
}: {
    label: ReactNode;
    value: ReactNode;
}) => (
    <div className="flex items-center justify-between">
        <Text>{label}</Text>
        <Text className="font-bold">{value}</Text>
    </div>
);

type RowProps = {
    selectedOffer: TFormalOffer | undefined;
    drawdownAmount: number | undefined;
};

export const InterestOnlyRepaymentRows = ({
    selectedOffer,
    drawdownAmount
}: RowProps) => {
    const { data } = useDrawdownRepaymentSchedule({
        offerId: selectedOffer?.offer_id ?? '',
        drawdownAmount: drawdownAmount ?? 0,
        enabled: Boolean(selectedOffer) && Boolean(drawdownAmount)
    });

    const interestOnlyMonths = selectedOffer
        ? getFormalOfferInterestOnlyPeriod(selectedOffer)
        : null;
    const fullTermMonths = selectedOffer?.duration
        ? getDurationInMonths(selectedOffer.duration)
        : null;

    let monthlyRepayment: number | null = null;
    let interestOnlyRepayment: number | null = null;

    if (data) {
        monthlyRepayment =
            getNonInterestOnlyPaymentAmount(data.schedule) ?? null;
        if (data.schedule.length > 0) {
            interestOnlyRepayment = data.schedule[0].total ?? null;
        }
    }

    const repaymentMonthsStart = interestOnlyMonths
        ? interestOnlyMonths + 1
        : null;
    const repaymentMonthsEnd = fullTermMonths ?? null;

    return (
        <>
            <SummaryRow
                label={
                    <FormattedMessage
                        id="_.loan_summary.monthly_repayment_io_period"
                        defaultMessage="Monthly repayment{br}for months {start}-{end}"
                        values={{
                            br: <br />,
                            start: 1,
                            end: interestOnlyMonths
                        }}
                    />
                }
                value={
                    interestOnlyRepayment !== null ? (
                        <FormattedCurrency value={interestOnlyRepayment} />
                    ) : (
                        '£--.--'
                    )
                }
            />
            <SummaryRow
                label={
                    <FormattedMessage
                        id="_.loan_summary.monthly_repayment_principal_period"
                        defaultMessage="Monthly repayment{br}for months {start}-{end}"
                        values={{
                            br: <br />,
                            start: repaymentMonthsStart,
                            end: repaymentMonthsEnd
                        }}
                    />
                }
                value={
                    monthlyRepayment !== null ? (
                        <FormattedCurrency value={monthlyRepayment} />
                    ) : (
                        '£--.--'
                    )
                }
            />
        </>
    );
};

export const InterestRateRow = ({ selectedOffer }: RowProps) => {
    const interestRate = selectedOffer?.interest_rates?.[0]?.rate ?? null;

    return (
        <SummaryRow
            label={
                <FormattedMessage
                    id="_.loan_summary.interest_rate"
                    defaultMessage="Interest rate /30 days:"
                />
            }
            value={
                interestRate !== null ? (
                    <VariableRate
                        minimumFractionDigits={2}
                        maximumFractionDigits={3}
                        value={interestRate}
                    />
                ) : (
                    '-.--%'
                )
            }
        />
    );
};

export const WithdrawalAmountRow = ({
    drawdownAmount
}: {
    drawdownAmount: number | undefined;
}) => (
    <SummaryRow
        label={
            <FormattedMessage
                id="_.loan_summary.withdrawal_amount"
                defaultMessage="Withdrawal amount:"
            />
        }
        value={
            drawdownAmount !== undefined ? (
                <FormattedCurrency value={drawdownAmount} />
            ) : (
                '£--.--'
            )
        }
    />
);

export const WithdrawalFeesRow = ({
    selectedOffer
}: {
    selectedOffer: TFormalOffer | undefined;
}) => {
    const feePercentage = selectedOffer?.withdrawal_fee_percentage ?? null;

    return (
        <SummaryRow
            label={
                <FormattedMessage
                    id="_.loan_summary.withdrawal_fees"
                    defaultMessage="Withdrawal fees:"
                />
            }
            value={
                feePercentage !== null
                    ? `${parseFloat((feePercentage * 100).toFixed(2))}%`
                    : '-%'
            }
        />
    );
};

export const FullTermDurationRow = ({ selectedOffer }: RowProps) => {
    const fullTermMonths = selectedOffer?.duration
        ? getDurationInMonths(selectedOffer.duration)
        : null;

    return (
        <SummaryRow
            label={
                <FormattedMessage
                    id="_.loan_summary.full_term_duration"
                    defaultMessage="Full term duration:"
                />
            }
            value={
                fullTermMonths !== null ? (
                    <FormattedMessage
                        id="_.loan_summary.full_term_months"
                        defaultMessage="{months} months"
                        values={{ months: fullTermMonths }}
                    />
                ) : (
                    '- months'
                )
            }
        />
    );
};

export const FullTermRepaymentRow = ({
    selectedOffer,
    drawdownAmount
}: RowProps) => {
    const { data } = useDrawdownRepaymentSchedule({
        offerId: selectedOffer?.offer_id ?? '',
        drawdownAmount: drawdownAmount ?? 0,
        enabled: Boolean(selectedOffer) && Boolean(drawdownAmount)
    });

    const totalRepayment = data?.summary.total_payable ?? null;

    return (
        <SummaryRow
            label={
                <FormattedMessage
                    id="_.loan_summary.full_term_repayment"
                    defaultMessage="Total repayable (if no early repayments):"
                />
            }
            value={
                totalRepayment !== null ? (
                    <FormattedCurrency value={totalRepayment} />
                ) : (
                    '£--.--'
                )
            }
        />
    );
};
